import React, { useState, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import GuideLogoWhite from '../OracleCommons/GuideLogoWhite'
import { setOracleGuideOnSideClosed } from '../../services/oracle/actions'
import { OracleIconButton } from '../OracleCommons/index'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'

export interface OracleGuideOnSideHeaderProps {
  onClose?: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  player?: any
}

export const OracleGuideOnSideHeader: React.FC<OracleGuideOnSideHeaderProps> = ({
  onClose,
  player,
}) => {
  const dispatch = useDispatch()
  const closeGuideLabel = useTranslatedText({ keys: ['gots', 'closeGuide'] })
  const playLabel = useTranslatedText({ keys: ['gots', 'play'] })
  const pauseLabel = useTranslatedText({ keys: ['gots', 'pause'] })
  const [isPaused, setIsPaused] = useState(false)

  // Sync play/pause state with player
  useEffect(() => {
    if (!player) return undefined

    const handlePlay = () => setIsPaused(false)
    const handlePause = () => setIsPaused(true)

    player.on('play', handlePlay)
    player.on('pause', handlePause)
    setIsPaused(player.paused())

    return () => {
      player.off('play', handlePlay)
      player.off('pause', handlePause)
    }
  }, [player])

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      // Fallback if no onClose provided
      dispatch(setOracleGuideOnSideClosed(true))
    }
  }

  const handlePlayPause = useCallback(() => {
    if (!player) return
    if (player.paused()) {
      player.play()
    } else {
      player.pause()
    }
  }, [player])

  return (
    <div className="oracle-guide-on-side-header">
      <div className="oracle-guide-on-side-header__top-bar">
        {/* Play/pause button - visible only in portrait mobile via CSS */}
        {player ? (
          <button
            type="button"
            className="oracle-guide-on-side-header__play-pause-button oracle-icon-button"
            onClick={handlePlayPause}
            aria-label={isPaused ? playLabel : pauseLabel}
          >
            {isPaused ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5,3 19,12 5,21" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <rect x="5" y="3" width="5" height="18" />
                <rect x="14" y="3" width="5" height="18" />
              </svg>
            )}
          </button>
        ) : null}
        <OracleIconButton icon="close" onClick={handleClose} aria-label={closeGuideLabel} />
      </div>
      <div className="oracle-guide-on-side-header__logo">
        <GuideLogoWhite className="oracle-guide-on-side-header__logo-component" />
      </div>
    </div>
  )
}
